/**
 * Nicotine River — product detail page enhancements.
 *
 * Four independent behaviours, each a no-op if its markup isn't present:
 *
 *   1. specTable — the Product Information / Compliance columns are authored in
 *      product metafields as "Label: Value" list items. Split them so they can
 *      be set as a real spec table. Purely additive: without this the list
 *      still reads correctly, it just isn't columnised.
 *
 *   2. buyBar — reveal the sticky buy bar once the real add-to-cart has
 *      scrolled out of view, and keep its price in step with the variant.
 *
 *   3. variantDropdownWidth — measure the variant field so its dropdown can be
 *      the same width. Without this the dropdown falls back to the theme's
 *      375px, which is narrower but not broken.
 *
 *   4. recommendations — fetch the "You may also like" band's products, which
 *      Shopify will only render through its recommendations endpoint. Without
 *      this the band removes itself, leaving the page as it was before.
 *
 * Nothing here touches the add-to-cart form, the [data-product-json] script or
 * any app-owned node.
 */
(function () {
  'use strict';

  /* ======================================================================
     1. Spec table
     ====================================================================== */

  function initSpecTable() {
    var items = document.querySelectorAll('.thcin li');
    if (!items.length) return;

    Array.prototype.forEach.call(items, function (li) {
      // Guard against double-processing if this ever runs twice.
      if (li.querySelector('.nr-spec__key')) return;

      // Only split on a colon in the label position. Values legitimately
      // contain colons (times, ratios), and "ISO 9001:2015 Certified: C042…"
      // must split on the *last* colon, not the one inside the standard name.
      var text = li.textContent.replace(/\u00a0/g, ' ').trim();
      var idx = text.lastIndexOf(':');

      // No colon, or a trailing colon with no value: leave as prose.
      if (idx === -1 || idx === text.length - 1) {
        li.classList.add('nr-spec--plain');
        return;
      }

      var key = text.slice(0, idx).trim();
      var val = text.slice(idx + 1).trim();

      if (!key || !val) {
        li.classList.add('nr-spec--plain');
        return;
      }

      // Rebuilding from textContent deliberately discards the inline
      // font-family/colour styling that the admin rich-text editor pastes in.
      // Any real markup in these items is a <span> carrying exactly that
      // styling, so nothing of value is lost.
      li.textContent = '';

      var keyEl = document.createElement('span');
      keyEl.className = 'nr-spec__key';
      keyEl.textContent = key;

      var valEl = document.createElement('span');
      valEl.className = 'nr-spec__val';
      valEl.textContent = val;

      li.appendChild(keyEl);
      li.appendChild(valEl);
    });
  }

  /* ======================================================================
     2. Sticky buy bar
     ====================================================================== */

  function initBuyBar() {
    var bar = document.querySelector('[data-nr-buybar]');
    if (!bar) return;

    var form = document.querySelector('.ProductForm');
    var atc = document.querySelector('.ProductForm__AddToCart');
    if (!form || !atc) {
      // No purchasable form on this template (coming-soon), so no bar.
      bar.parentNode.removeChild(bar);
      return;
    }

    var priceOut = bar.querySelector('[data-nr-buybar-price]');
    var variantOut = bar.querySelector('[data-nr-buybar-variant]');
    var jump = bar.querySelector('[data-nr-buybar-jump]');

    // --- anchor target -----------------------------------------------------
    // The href points at #nr-buy; put that id on the form so the jump lands on
    // the control rather than the top of the page.
    if (!document.getElementById('nr-buy')) form.id = 'nr-buy';

    if (jump) {
      jump.addEventListener('click', function (event) {
        event.preventDefault();
        form.scrollIntoView({
          behavior: prefersReduced() ? 'auto' : 'smooth',
          block: 'center'
        });
        // Hand the keyboard over to the real button.
        try {
          atc.focus({ preventScroll: true });
        } catch (e) {
          atc.focus();
        }
      });
    }

    // --- visibility --------------------------------------------------------
    function show(on) {
      bar.classList.toggle('is-visible', on);
      // Hidden from assistive tech while offscreen: the same price and button
      // already exist in the form, and announcing a duplicate is noise.
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        // Only once the button has left upward — no bar while the reader is
        // still above the buy box and heading towards it.
        show(!e.isIntersecting && e.boundingClientRect.top < 0);
      }, { threshold: 0 }).observe(atc);
    }

    // --- keep price and variant in step -----------------------------------
    var priceSrc = document.querySelector('.ProductMeta__Price:not(.Price--compareAt)');

    function sync() {
      if (priceSrc && priceOut) priceOut.textContent = priceSrc.textContent.trim();

      if (variantOut) {
        // Read the live selection out of the form rather than tracking variant
        // objects ourselves, so this cannot disagree with what is submitted.
        var selected = [];
        Array.prototype.forEach.call(
          form.querySelectorAll('[data-single-option-selector], .ProductForm__Item'),
          function (el) {
            var v = (el.getAttribute('data-value') || el.textContent || '').trim();
            // Dropdown triggers render as "Volume: 1 Gallon".
            var parts = v.split(':');
            if (parts.length > 1) v = parts.slice(1).join(':').trim();
            if (v && selected.indexOf(v) === -1) selected.push(v);
          }
        );
        if (selected.length) variantOut.textContent = selected.join(' · ');
      }
    }

    // The theme fires this on every variant change.
    document.addEventListener('variant:changed', sync);

    // Belt and braces: the price node is also rewritten directly by the theme
    // and by the wholesale pricing app, neither of which fires that event.
    if (priceSrc && 'MutationObserver' in window) {
      new MutationObserver(sync).observe(priceSrc, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    sync();
  }

  /* ======================================================================
     3. Variant dropdown width
     ====================================================================== */

  /**
   * Publish the width of the variant field so the open dropdown can match it.
   *
   * The panel is anchored under its field by theme.min.js, which aligns their
   * right edges. Equal widths therefore line up the left edges too, and the panel
   * reads as an extension of the field instead of a narrower box tucked under one
   * corner of it.
   *
   * Why a custom property rather than the panel's own width:
   *
   *   - The theme rewrites the panel's entire style attribute on every open
   *     (setAttribute('style', 'top: …; right: …')), so an inline width set here
   *     would survive until the first click and no longer.
   *   - CSS cannot read the field's width, and the panel is not inside the field
   *     to inherit it — Prestige collects every panel into one container at the
   *     end of the section, away from the form.
   *
   * So the measurement goes on the root element, where it is out of reach of that
   * rewrite and still inherits down to wherever the panel has been parked.
   * nr-pages.css consumes it inside the same 1008px query the theme's own JS
   * switches on, and falls back to the theme's 375px if this never runs — which is
   * also what the homepage's featured-product section gets, since this file is
   * only loaded on product pages.
   */
  function initVariantDropdownWidth() {
    // Every option field is the full width of the info column, so one
    // measurement covers a product with several of them.
    var field = document.querySelector('.ProductForm .ProductForm__Item[aria-controls]');
    if (!field) return;

    var queued = false;

    function measure() {
      queued = false;
      var w = field.getBoundingClientRect().width;
      // Zero while the field is display:none — inside a closed drawer, say.
      // Keeping the last good value beats collapsing the panel to nothing.
      if (w > 0) document.documentElement.style.setProperty('--nr-vsel-width', w + 'px');
    }

    measure();

    window.addEventListener('resize', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    });
  }

  /* ======================================================================
     4. Recommendations
     ----------------------------------------------------------------------
   * Shopify only fills the `recommendations` object when a section is requested
   * through the recommendations endpoint, so sections/nr-recommendations renders
   * a heading over placeholder cards and this fetches the same section back with
   * products in it. The URL, including the product id and the intent, is built in
   * Liquid and read off the element.
   *
   * Failure is silent and tidy: no answer, or an answer with no products in it,
   * removes the band rather than leaving placeholders behind.
     ====================================================================== */
  function initRecommendations() {
    var section = document.querySelector('[data-nr-recs]');
    if (!section) return;

    var url = section.getAttribute('data-nr-recs-url');
    // Already holds real cards — a second run after a section reload in the
    // theme editor, or a merchant previewing the fetched render.
    if (!url) return;

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        var fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-nr-recs]');

        // The section renders nothing when it has no products to show, so an
        // absent element here is the answer "there is nothing to recommend".
        if (!fresh) {
          section.remove();
          return;
        }

        section.replaceWith(fresh);
        initCardRatings();
      })
      .catch(function () {
        section.remove();
      });
  }

  /* Yotpo binds its widgets by scanning the document as it loads, so the star
   * ratings inside cards that arrive later start out empty. initWidgets() is the
   * app's own re-scan, and it skips widgets already marked loaded — the rating
   * under the product title and the review widget further down are left alone.
   *
   * Guarded, because it is one of the app's internals rather than a documented
   * hook: if a future widget version drops it, these cards simply carry no stars,
   * which the card already handles. */
  function initCardRatings() {
    try {
      var yotpo = window.yotpoWidgetsContainer;
      if (yotpo && typeof yotpo.initWidgets === 'function') yotpo.initWidgets();
    } catch (error) {
      /* Decoration. Nothing else on the card depends on it. */
    }
  }

  function prefersReduced() {
    return !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function init() {
    initSpecTable();
    initBuyBar();
    initVariantDropdownWidth();
    initRecommendations();
  }

  // The theme editor re-renders a section on its own, which restores the
  // placeholders; without this they would sit there until the page was reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector &&
        event.target.querySelector('[data-nr-recs]')) {
      initRecommendations();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
